import { Box, InputAdornment, Menu, MenuItem, TextField, Tooltip } from '@mui/material';
import {
  GridApi,
  GridCsvExportMenuItem,
  GridCsvExportOptions,
  GridExportDisplayOptions,
  GridExportMenuItemProps,
  GridToolbarProps,
  GridToolbarQuickFilterProps,
  QuickFilter,
  QuickFilterClear,
  QuickFilterControl,
  Toolbar,
  ToolbarButton,
  ToolbarPropsOverrides,
  gridFilteredSortedRowIdsSelector,
  gridVisibleColumnFieldsSelector,
  useGridApiContext,
} from '@mui/x-data-grid';
import { Download, Search, X } from 'lucide-react';
import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

interface JsonExportProps extends GridExportMenuItemProps<{}> {
  fileName?: string;
}

//#region default components
const JsonExportMenuItem: React.FC<JsonExportProps> = ({ fileName, ...props }) => {
  const apiRef = useGridApiContext();

  const getRows = (apiRef: React.MutableRefObject<GridApi>) => {
    // Select rows and columns
    const filteredSortedRowIds = gridFilteredSortedRowIdsSelector(apiRef);
    const visibleColumnsField = gridVisibleColumnFieldsSelector(apiRef);

    // Format the data. Here we only keep the value
    const data = filteredSortedRowIds.map((id) => {
      const row: Record<string, any> = {};
      visibleColumnsField.forEach((field) => {
        row[field] = apiRef.current?.getCellParams(id, field).value;
        if (typeof row[field] === 'object' && row[field] !== null) {
          row[field] = row[field].id; // set treatmentId to the treatment's id instead of object id and label
        }
      });
      return row;
    });

    // Stringify with some indentation
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/JSON/stringify#parameters
    return data;
  };

  const exportBlob = (blob: Blob, filename: string) => {
    // Save the blob in a json file
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();

    setTimeout(() => {
      URL.revokeObjectURL(url);
    });
  };

  const { hideMenu } = props;

  return (
    <MenuItem
      onClick={() => {
        const data = getRows(apiRef);

        const blob = new Blob([JSON.stringify(data, null, 2)], {
          type: 'text/json',
        });
        exportBlob(blob, `${fileName}.json`);

        // Hide the export menu after the export
        hideMenu?.();
      }}>
      <FormattedMessage id={`general.exportJSONBtn`} />
    </MenuItem>
  );
};

const SearchFilter: React.FC<GridToolbarQuickFilterProps> = ({ ...props }) => {
  const intl = useIntl();

  return (
    <QuickFilter {...props}>
      <QuickFilterControl
        render={({ ref, ...controlProps }, state) => (
          <TextField
            {...controlProps}
            inputRef={ref}
            aria-label="Search"
            size="small"
            label={intl.formatMessage({ id: 'general.search' })}
            slotProps={{
              input: {
                endAdornment: state.value ? (
                  <InputAdornment position="end">
                    <QuickFilterClear edge="end" size="small" aria-label="Clear search" material={{ sx: { marginRight: -0.75 } }}>
                      <X fontSize="small" />
                    </QuickFilterClear>
                  </InputAdornment>
                ) : (
                  <InputAdornment position="end">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
                ...controlProps.slotProps?.input,
              },
              ...controlProps.slotProps,
            }}
          />
        )}
      />
    </QuickFilter>
  );
};

interface ExportMenuProps {
  csvOptions: (GridCsvExportOptions & GridExportDisplayOptions) | undefined;
}

const ExportMenu: React.FC<ExportMenuProps> = ({ csvOptions }) => {
  const [open, setOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  return (
    <React.Fragment>
      <Tooltip title="Export">
        <ToolbarButton
          ref={triggerRef}
          id="export-menu-trigger"
          aria-controls="export-menu"
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={() => setOpen(true)}>
          <Download />
        </ToolbarButton>
      </Tooltip>
      <Menu
        id="export-menu"
        anchorEl={triggerRef.current}
        open={open}
        onClose={() => setOpen(false)}
        slotProps={{
          paper: (props) => ({
            sx: {
              ...props.sx,
              padding: 0,
            },
          }),
          list: {
            'aria-labelledby': 'export-menu-trigger',
          },
        }}>
        <GridCsvExportMenuItem options={csvOptions} />
        <JsonExportMenuItem fileName={csvOptions?.fileName} />
      </Menu>
    </React.Fragment>
  );
};

//#endregion default components

interface CustomToolbarProps extends Partial<GridToolbarProps & ToolbarPropsOverrides>, React.PropsWithChildren {}

/**
 * DataGridToolbar that supports custom children. Children are passed as part of the toolbar's `slotProps` when using DataGrid.
 */
const DataGridToolbar: React.FC<CustomToolbarProps> = ({
  children,
  csvOptions,
  printOptions,
  slotProps,
  quickFilterProps,
  ...props
}) => {
  return (
    <Toolbar {...props}>
      <Box width={'100%'} display={'flex'} flexDirection={'row'} justifyContent={'space-between'} alignItems={'center'}>
        <Box display={'flex'} flexDirection={'row'}>
          {children}
        </Box>
        <Box display={'flex'} flexDirection={'row'} gap={4}>
          <SearchFilter {...quickFilterProps} />
          <ExportMenu csvOptions={csvOptions} />
        </Box>
      </Box>
    </Toolbar>
  );
};

export default React.memo(DataGridToolbar);
